// Queue for loadFeatures calls before Shopify is ready
const loadFeaturesQueue = [];
const originalLoadFeatures = window.Shopify?.loadFeatures;

if (!window.Shopify) {
    window.Shopify = {};
}

if (!window.Shopify.loadFeatures) {
    window.Shopify.loadFeatures = function(features, callback) {
        loadFeaturesQueue.push({ features, callback });
    };
}

const processLoadFeaturesQueue = () => {
    if (typeof originalLoadFeatures === 'function') {
        window.Shopify.loadFeatures = originalLoadFeatures;
    }

    const realLoadFeatures = window.Shopify.loadFeatures;
    if (typeof realLoadFeatures === 'function' && loadFeaturesQueue.length > 0) {
        loadFeaturesQueue.forEach(({ features, callback }) => {
            realLoadFeatures.call(window.Shopify, features, callback);
        });
        loadFeaturesQueue.length = 0;
    }
};

const priorityShopifyWebkit = () => {
    const template = document.getElementById('content_for_header');
    if (!template) return;
    const scriptInTemplate = template.content?.querySelector('#web-pixels-manager-setup');
    if (!scriptInTemplate) return;

    const newScript = document.createElement('script');
    newScript.id = scriptInTemplate.id;
    newScript.textContent = scriptInTemplate.textContent;
    for (const attr of scriptInTemplate.attributes) {
        if (attr.name !== 'id') {
            newScript.setAttribute(attr.name, attr.value);
        }
    }
    template.content.insertBefore(newScript, template.content.firstChild);
    scriptInTemplate.remove();
};

const EVENT = {
    delayed: 'Delayed',
    interactive: 'FirstInteractive',
    load: 'load'
};

class DelayScript extends HTMLElement {
    constructor() {
        super();
        this._loaded = false;
    }

    connectedCallback() {
        const phase = this.getAttribute('phase') || 'load';
        const readyState = document.readyState;

        const shouldRunImmediately =
            (phase === 'load' && readyState === 'complete') ||
            (phase === 'interactive' && window.optPef && window.optPef.__is_interactive);

        if (shouldRunImmediately) {
            this._load();
        } else {
            window.addEventListener(EVENT[phase], () => setTimeout(() => this._load(), 0), { once: true });
        }
    }

    _load() {
        if (this._loaded) return;
        this._loaded = true;

        const script = document.createElement('script');

        Array.from(this.attributes).forEach(attr => {
            if (!['phase', 'metric'].includes(attr.name)) {
                script.setAttribute(attr.name, attr.value);
            }
        });

        if (!script.hasAttribute('src') && this.hasAttribute('src')) {
            script.src = this.getAttribute('src');
        }

        script.textContent = this.textContent;

        script.onload = () => {
            this.dispatchEvent(new Event('load'));
        };

        this.replaceWith(script);
    }
}

customElements.define('delay-script', DelayScript);


class DelayStylesheet extends HTMLElement {
    constructor() {
        super();
        this._loaded = false;
    }

    connectedCallback() {
        if (document.readyState !== 'loading') {
            this._load();
        } else {
            window.addEventListener('DOMContentLoaded', () => setTimeout(() => this._load(), 0), { once: true });
        }
    }

    _load() {
        if (this._loaded) return;
        this._loaded = true;

        const link = document.createElement('link');
        link.rel = 'stylesheet';

        Array.from(this.attributes).forEach(attr => {
            if (attr.name !== 'rel') {
                link.setAttribute(attr.name, attr.value);
            }
        });

        this.replaceWith(link);
    }
}
customElements.define('delay-stylesheet', DelayStylesheet);

(function () {
    'use strict';
    let delayedTimeout;

    const handleDelayed = () => {
        const template = document.getElementById('content_for_header');
        if (!template || !template.content) return;
        const elements = Array.from(template.content.children);

        elements.forEach((element) => {
            let newEl = element.cloneNode(true);
            if (newEl.tagName.toLowerCase() === 'script') {
                let textContent = element.innerHTML;
                const event = window.optPef.isFirstInteractive ? 'FirstInteractive' : 'ShopifyConnected';
                if (textContent.includes('DOMContentLoaded')) {
                    textContent = textContent.replace(/DOMContentLoaded/g, 'ShopifyConnected');
                }
                if (textContent.includes(`window.addEventListener('load'`)) {
                    textContent = textContent.replace(/window\.addEventListener\('load'/g, `window.addEventListener('${event}'`);
                }
                newEl.textContent = textContent;
            }
            document.head.appendChild(newEl);
        });
        template.content.innerHTML = '';

        setTimeout(() => {
            processLoadFeaturesQueue();
            document.dispatchEvent(new Event("ShopifyConnected"));
        }, 500);
        window.optPef.isExecuteDelayed = true;
    };

    window.addEventListener("Delayed", function () {
        if (window.optPef.isExecuteDelayed) return;
        handleDelayed();
    }, false);

    const handleFirstInteraction = () => {
        clearTimeout(delayedTimeout);
        window.dispatchEvent(new Event("FirstInteractive"));
        window.optPef.interactive_events.forEach(function (event) {
            window.removeEventListener(event, handleFirstInteraction, false);
        });
    }

    const executeDelayedScript = () => {
        priorityShopifyWebkit();

        window.dispatchEvent(new Event("Delayed"));

        if (window.optPef.__is_interactive) {
            handleFirstInteraction();
        } else {
            delayedTimeout = setTimeout(() => {
                if (window.optPef.__is_interactive) return;
                handleFirstInteraction();
            }, 20000);
            window.optPef.interactive_events.forEach(function (event) {
                window.addEventListener(event, handleFirstInteraction, false);
            });
        }
    }

    window.addEventListener("critical_resources-loaded", function () {
        executeDelayedScript();
    }, false);

})();

function getStylesheet(href) {
    const url = new URL(href, "https://cdn.shopify.com/");
    if (!url.searchParams.has('v')) url.searchParams.set('v', Date.now());

    return new Promise(resolve => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = url.toString();
        link.onload = () => resolve(link);
        link.onerror = () => resolve(link);
        document.head.appendChild(link);
    });
}

function getScript(src) {
    return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.onload = () => resolve(s);
        s.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(s);
    });
}